// Even after installing @types/google-map-react, we don't have good typing
//  for the google-map-react library, so I am defining our own types here.
//
// The documentation I am working off is here:
//  https://developers.google.com/maps/documentation/javascript/reference
//
import { LatLngStr } from './stopTypes';

//////////////////////////////////////////////////////////////////////////////
// region Coordinates.
//////////////////////////////////////////////////////////////////////////////

type LatLngNumber = {
    lat: number;
    lng: number;
};

export const latLngNumberFromLatLngStr = (latLngStr: LatLngStr): LatLngNumber => ({
    lat: parseFloat(latLngStr.lat),
    lng: parseFloat(latLngStr.lng),
});

type GoogleMapsLatLng = {
    lat: () => number;
    lng: () => number;
};

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Directions Service.
//
// This is what fetches directions from the Google Maps API.
//////////////////////////////////////////////////////////////////////////////

// Must be something like `${lat}, ${lng}`;
type LatLngForDirections = string;

export const formatLatLangForDirections = ({ lat, lng }: LatLngStr): LatLngForDirections =>
    `${lat}, ${lng}`;

export type DirectionsTravelMode = 'DRIVING' | 'BICYCLING' | 'TRANSIT' | 'WALKING';

export type DirectionsRequest = {
    origin: LatLngForDirections;
    destination: LatLngForDirections;
    waypoints: { location: LatLngForDirections; stopover: boolean }[];
    travelMode: DirectionsTravelMode;
    optimizeWaypoints: boolean;
};

export type DirectionsResult = {
    routes: {
        legs: {
            start_location: GoogleMapsLatLng;
            end_location: GoogleMapsLatLng;
        }[];
    }[];
};

type DirectionRequestCallback = (result: DirectionsResult, status: 'OK' | string) => void;

export declare class DirectionsService {
    constructor();
    route(request: DirectionsRequest, callback: DirectionRequestCallback): void;
}

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Directions Renderer.
//
// This is what draws the fetched directions on the map.
//////////////////////////////////////////////////////////////////////////////

export declare class DirectionsRenderer {
    constructor(options: {
        polylineOptions: {
            strokeColor: string;
            strokeOpacity: string;
            strokeWeight: number;
        };
    });
    setOptions(options: { suppressMarkers: boolean; preserveViewport: boolean }): void;
    setMap(map: Map): void;
    setDirections(result: DirectionsResult): void;
}

// endregion
//////////////////////////////////////////////////////////////////////////////
// region LayLngBounds.
//////////////////////////////////////////////////////////////////////////////

// https://developers.google.com/maps/documentation/javascript/reference/top-level#CoreLibrary.LatLngBounds
declare class LatLngBounds {
    constructor();
    extend(latLng: LatLngNumber): void;
    getCenter(): GoogleMapsLatLng;
}

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Map and Maps.
//////////////////////////////////////////////////////////////////////////////

export declare class Polyline {
    constructor(options: {
        path: { lat: number; lng: number }[];
        geodesic?: boolean;
        strokeColor?: string;
        strokeOpacity?: number;
        strokeWeight?: number;
    });
    setMap(map: Map | null): void;
}

type MapEventListener = { remove: () => void };

type Projection = {
    fromLatLngToPoint(latLng: GoogleMapsLatLng): { x: number; y: number };
    fromPointToLatLng(point: { x: number; y: number }): GoogleMapsLatLng;
};

export type Maps = {
    TravelMode: DirectionsTravelMode;
    // Without the `typeof` in `typeof DirectionsService`, the type gets
    //  interpreted as an instance of the class rather than the class itself.
    DirectionsService: typeof DirectionsService;
    DirectionsRenderer: typeof DirectionsRenderer;
    LatLngBounds: typeof LatLngBounds;
    Polyline: typeof Polyline;
    LatLng: new (lat: number, lng: number) => GoogleMapsLatLng;
    Point: new (x: number, y: number) => { x: number; y: number };
    event: {
        addListenerOnce: (map: Map, eventName: 'bounds_changed', callback: () => void) => void;
        addListener: (target: Map, eventName: string, callback: () => void) => MapEventListener;
    };
};

export type Map = {
    getZoom(): number;
    setZoom(zoom: number): void;
    setCenter(latLng: GoogleMapsLatLng): void;
    fitBounds(bounds: LatLngBounds): void;
    getProjection(): Projection | null;
    addListener(event: string, handler: () => void): MapEventListener;
};

// endregion
